import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, Globe, Phone } from 'lucide-react';
import { useAdminBasePath } from '../../hooks/useAdminBasePath';

/**
 * Settings hub — entry points for platform configuration (invoice templates, etc.).
 */
export const SettingsPage: React.FC = () => {
  const base = useAdminBasePath();
  const isEnterprise = true;

  const cardClass = isEnterprise
    ? 'rounded-xl border border-enterprise-200 bg-white p-6 shadow-sm transition hover:border-enterprise-300 hover:shadow-md'
    : 'rounded-lg border border-admin-200 bg-white p-6 shadow-sm transition hover:border-admin-300 hover:shadow-md';

  const titleClass = isEnterprise ? 'text-lg font-semibold text-enterprise-900' : 'text-lg font-semibold text-admin-900';
  const descClass = isEnterprise ? 'mt-1 text-sm text-enterprise-600' : 'mt-1 text-sm text-admin-600';
  const linkClass = isEnterprise
    ? 'mt-4 inline-flex items-center gap-2 text-sm font-medium text-enterprise-accent hover:text-emerald-800'
    : 'mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-800';

  return (
    <div>
      <div className="mb-8">
        <h1 className={isEnterprise ? 'text-2xl font-bold text-enterprise-900' : 'text-2xl font-bold text-admin-900'}>
          Settings
        </h1>
        <p className={isEnterprise ? 'mt-1 text-enterprise-600' : 'mt-1 text-admin-600'}>
          Platform-wide configuration. Changes apply according to each feature&apos;s scope (global or per workspace).
        </p>
      </div>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <div className={cardClass}>
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
            <FileText className="h-5 w-5" />
          </div>
          <h2 className={`mt-4 ${titleClass}`}>Invoice templates</h2>
          <p className={descClass}>
            Create and manage PDF invoice layouts: branding, legal copy, tax display, and defaults for the platform or a
            workspace.
          </p>
          <Link to={`${base}/settings/invoice-templates`} className={linkClass}>
            Open invoice templates
            <span aria-hidden>→</span>
          </Link>
        </div>
        <div className={cardClass}>
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
            <Phone className="h-5 w-5" />
          </div>
          <h2 className={`mt-4 ${titleClass}`}>Notification senders</h2>
          <p className={descClass}>
            Default Indian and international phone numbers for workspace user notification SMS and voice calls.
          </p>
          <Link to={`${base}/settings/notification-senders`} className={linkClass}>
            Open notification senders
            <span aria-hidden>→</span>
          </Link>
        </div>
        <div className={cardClass}>
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
            <Globe className="h-5 w-5" />
          </div>
          <h2 className={`mt-4 ${titleClass}`}>Firecrawl API</h2>
          <p className={descClass}>
            Base URL for Knowledge Hub website page discovery and content extraction across all workspaces.
          </p>
          <Link to={`${base}/settings/firecrawl`} className={linkClass}>
            Open Firecrawl settings
            <span aria-hidden>→</span>
          </Link>
        </div>
        <div className={cardClass}>
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
            <Globe className="h-5 w-5" />
          </div>
          <h2 className={`mt-4 ${titleClass}`}>SearXNG (Web search)</h2>
          <p className={descClass}>
            Metasearch base URL for AI Employee web search and live research tools.
          </p>
          <Link to={`${base}/settings/searxng`} className={linkClass}>
            Open SearXNG settings
            <span aria-hidden>→</span>
          </Link>
        </div>
      </div>
    </div>
  );
};
